import { format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

// Date formatting utility functions
export const formatDateForDisplay = (dateString: string | null, language: string = 'en'): string => {
  if (!dateString) return '';
  
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const year = date.getFullYear();
  
  if (language === 'zh') {
    return `${year}年${parseInt(month)}月${parseInt(day)}日`;
  }
  
  return `${month}/${day}/${year}`;
};

export const parseDateInput = (input: string, language: string = 'en'): string | null => {
  if (!input.trim()) return null;
  
  let month: number, day: number, year: number;
  
  if (language === 'zh') {
    // Parse format: YYYY年MM月DD日
    const match = input.match(/(\d{4})年(\d{1,2})月(\d{1,2})日/);
    if (!match) return null;
    
    year = parseInt(match[1]);
    month = parseInt(match[2]);
    day = parseInt(match[3]);
  } else {
    // Parse format: MM/DD/YYYY
    const parts = input.split('/');
    if (parts.length !== 3) return null;
    
    month = parseInt(parts[0]);
    day = parseInt(parts[1]);
    year = parseInt(parts[2]);
  }
  
  // Validate date
  if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1900 || year > new Date().getFullYear()) {
    return null;
  }
  
  // Check if date is valid
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }
  
  // Return in ISO format (YYYY-MM-DD)
  return `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
};

export const getDatePlaceholder = (language: string = 'en'): string => {
  return language === 'zh' ? 'YYYY年MM月DD日' : 'MM/DD/YYYY';
};

// Safely validate and format date values to prevent "Invalid time value" errors
export const validateAndFormatDate = (dateInput: string | Date | null | undefined): Date | null => {
  if (!dateInput) return null;
  
  try {
    const date = new Date(dateInput);
    if (isNaN(date.getTime())) {
      console.warn('Invalid date value:', dateInput);
      return null;
    }
    return date;
  } catch (error) {
    console.error('Error parsing date:', dateInput, error);
    return null;
  }
};

// Safe wrapper for date-fns format function
export const safeFormat = (date: string | Date | null | undefined, formatString: string, options?: any): string => {
  const validDate = validateAndFormatDate(date);
  if (!validDate) return 'Invalid Date';
  
  try {
    return format(validDate, formatString, options);
  } catch (error) {
    console.error('Error formatting date with date-fns:', error);
    return 'Date Error';
  }
};

// Safe wrapper for date-fns-tz formatInTimeZone function
export const safeFormatInTimeZone = (date: string | Date | null | undefined, timeZone: string, formatString: string, options?: any): string => {
  const validDate = validateAndFormatDate(date);
  if (!validDate) return 'Invalid Date';
  
  try {
    return formatInTimeZone(validDate, timeZone, formatString, options);
  } catch (error) {
    console.error('Error formatting date in timezone:', error);
    return 'Date Error';
  }
};

// Safe date formatting that handles invalid dates gracefully
export const safeFormatDate = (dateInput: string | Date | null | undefined, formatString: string = 'MMM dd, yyyy'): string => {
  const validDate = validateAndFormatDate(dateInput);
  if (!validDate) return 'Invalid Date';
  
  try {
    return formatString.includes('yyyy') 
      ? validDate.toLocaleDateString() 
      : validDate.toLocaleString();
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Date Error';
  }
};